import { useHead, useSeoMeta } from "@unhead/react"
import Navbar from "@/components/Navbar"
import CattlePerAcreCalculator from "@/components/CattlePerAcreCalculator"
import CattlePerAcreGuide from "@/components/CattlePerAcreGuide"
import AboutSection from "@/components/AboutSection"
import QuickLinksFooter from "@/components/QuickLinksFooter"

function CattlePerAcre() {
  useSeoMeta({
    title: 'Cattle Per Acre Calculator | Stocking Rate Tool',
    description: "Use our free cattle per acre calculator to determine the optimal stocking rate for your pasture. Factor in forage quality, rainfall, cattle weight, and grazing season for accurate results.",
    ogType: 'website',
    ogTitle: 'Cattle Per Acre Calculator | Stocking Rate Tool',
    ogDescription: "Determine the optimal stocking rate for your pasture. Free AUM-based cattle per acre calculator.",
    ogImage: 'https://www.cowcalculator.com/og-gestation.jpg',
    ogUrl: 'https://www.cowcalculator.com/cattle-per-acre-calculator',
    twitterCard: 'summary_large_image',
    twitterTitle: 'Cattle Per Acre Calculator | Stocking Rate Tool',
    twitterDescription: "Determine the optimal stocking rate for your pasture. Free AUM-based cattle per acre calculator.",
    twitterImage: 'https://www.cowcalculator.com/og-gestation.jpg',
  })

  useHead({
    link: [{ rel: 'canonical', href: 'https://www.cowcalculator.com/cattle-per-acre-calculator' }],
  })

  return (
    <div className="min-h-screen bg-[#FEFAE0]">
      <Navbar />
      <main>
        {/* Hero Banner */}
        <section className="relative bg-[#283618] overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{
              backgroundImage: "radial-gradient(circle at 20% 50%, #40916C 1px, transparent 1px), radial-gradient(circle at 80% 20%, #40916C 1px, transparent 1px)",
              backgroundSize: "60px 60px, 80px 80px",
            }} />
          </div>
          <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20 text-center">
            <div className="inline-flex items-center gap-2 bg-[#40916C]/30 backdrop-blur-sm border border-[#40916C]/40 rounded-full px-4 py-1.5 mb-5">
              <div className="w-2 h-2 rounded-full bg-[#B7E4C7] animate-pulse" />
              <span className="text-[#B7E4C7] text-xs sm:text-sm font-medium tracking-wide">
                Land Management Tool
              </span>
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#FEFAE0] leading-tight">
              Cattle Per Acre{" "}
              <span className="text-[#D4A373]">Calculator</span>
            </h1>
            <p className="mt-4 sm:mt-5 text-base sm:text-lg text-[#B7E4C7]/90 max-w-2xl mx-auto leading-relaxed">
              Determine the optimal <strong>stocking rate</strong> for your pasture. Calculate how many cattle your land can sustainably support based on forage quality, rainfall, and grazing season.
            </p>
            <div className="mt-6 flex flex-wrap items-center justify-center gap-6">
              {[
                { value: "Free", label: "No Cost" },
                { value: "AUM", label: "Based Calculations" },
                { value: "5+", label: "Forage Types" },
              ].map((stat) => (
                <div key={stat.label} className="text-center">
                  <div className="text-lg sm:text-xl font-bold text-[#D4A373]">{stat.value}</div>
                  <div className="text-xs text-[#B7E4C7]/60 mt-0.5">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
          {/* Bottom wave */}
          <div className="absolute bottom-0 left-0 right-0">
            <svg viewBox="0 0 1440 48" fill="none" className="w-full">
              <path d="M0 48L60 40C120 32 240 16 360 12C480 8 600 16 720 20C840 24 960 24 1080 20C1200 16 1320 8 1380 4L1440 0V48H0Z" fill="#FEFAE0" />
            </svg>
          </div>
        </section>

        <CattlePerAcreCalculator />
        <CattlePerAcreGuide />
        <AboutSection />
      </main>
      <QuickLinksFooter />
    </div>
  )
}

export default CattlePerAcre
